import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { stripeConnectApi } from '../../api';
import LoadingSpinner from '../../shared/components/LoadingSpinner';

interface SetupStripeProps {
  businessId: string;
  facilityId: string;
}

export default function SetupStripe({ businessId, facilityId }: SetupStripeProps) {
  const { user, userDataLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [showConfirmReconnect, setShowConfirmReconnect] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  useEffect(() => {
    const checkStripeStatus = async () => {
      if (!businessId || !facilityId) {
        setCheckingStatus(false);
        return;
      }

      try {
        const stripeStatus = await stripeConnectApi.getFacilityStripeStatus(businessId, facilityId);
        setIsConnected(stripeStatus.has_stripe);
        if (stripeStatus.has_stripe) {
          setSuccess('Your facility is connected to Stripe.');
        }
      } catch (err) {
        console.error('Error checking Stripe status:', err);
        setError('Failed to check Stripe connection status.');
      } finally {
        setCheckingStatus(false);
      }
    };

    checkStripeStatus();
  }, [user?.facilityId, businessId, facilityId]);

  const handleOnboard = async (forceReconnect = false) => {
    console.log('Current user data:', user);
    
    if (!user) {
      setError('Please log in to continue.');
      return;
    }

    if (!facilityId) {
      setError('No facility ID found. Please ensure your account is properly set up with a facility.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);
      
      // Step 1: Create Stripe Connect account
      const accountData = await stripeConnectApi.onboardBusiness(businessId, facilityId);
      
      // Handle already connected case
      if (accountData.message === "Facility already onboarded") {
        setIsConnected(true);
        if (!forceReconnect) {
          setSuccess("Your facility is already connected to Stripe.");
          setShowConfirmReconnect(true);
          setIsLoading(false);
          return;
        }
      }

      // Step 2: Get account link for onboarding
      const linkData = await stripeConnectApi.createAccountLink(businessId, facilityId);
      if (!linkData.url) {
        throw new Error('No onboarding URL received');
      }

      // Open Stripe onboarding in a new tab
      window.open(linkData.url, '_blank');
      
      // Show appropriate success message
      if (forceReconnect) {
        setSuccess('Stripe account settings opened in a new tab. You can update your account settings there.');
      } else {
        setSuccess('Stripe onboarding opened in a new tab. Please complete the process there.');
      }
      
      // Reset confirm dialog
      setShowConfirmReconnect(false);
    } catch (err: any) {
      console.error('Stripe onboarding error:', err);
      setError(err.message || 'An error occurred while setting up Stripe.');
    } finally {
      setIsLoading(false);
    }
  };

  if (userDataLoading || checkingStatus) {
    return <LoadingSpinner />;
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded" role="alert">
          <p>{error}</p>
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded" role="alert">
          <p>{success}</p>
        </div>
      )}
      
      {showConfirmReconnect ? (
        <div className="flex flex-col gap-4">
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
            <p>Your facility is already connected to Stripe. Would you like to:</p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={() => handleOnboard(true)}
              disabled={isLoading}
              className="px-4 py-2 rounded bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Update Stripe Settings
            </button>
            <button
              onClick={() => setShowConfirmReconnect(false)}
              className="px-4 py-2 rounded bg-gray-600 text-white hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => handleOnboard(false)}
          disabled={isLoading || userDataLoading}
          className={`px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isLoading ? 'Connecting...' : isConnected ? 'Update Stripe Settings' : 'Connect with Stripe'}
        </button>
      )}
    </div>
  );
} 